import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { PACKAGE_IMAGES } from "@/lib/images";
import Reveal from "@/components/Reveal";
import { Check } from "lucide-react";

const KEYS = ["bronze", "silber", "gold"];

// Bundled fallbacks so prices + Gold recommendation always render,
// even if the /api/content fetch is slow or fails. Admin content overrides these.
const DEFAULT_PACKAGES = {
  bronze: { price: "CHF 2'750.–", highlight: false },
  silber: { price: "CHF 4'500.–", highlight: false },
  gold: { price: "CHF 6'200.–", highlight: true },
};

export default function PackagesGrid({ showNote = true }) {
  const { t } = useLanguage();
  const { content } = useContent();
  const prices = content.packages || {};

  return (
    <div>
      <div className="grid md:grid-cols-3 gap-8 lg:gap-6 items-stretch">
        {KEYS.map((key, i) => {
          const pkg = t(`packages.items.${key}`);
          const cfg = prices[key] || DEFAULT_PACKAGES[key];
          const price = cfg?.price || DEFAULT_PACKAGES[key].price;
          const highlight = cfg?.highlight ?? DEFAULT_PACKAGES[key].highlight;
          return (
            <Reveal key={key} delay={i * 120} className="h-full">
              <div
                data-testid={`package-${key}`}
                className={`h-full flex flex-col bg-card border p-8 lg:p-10 transition-all duration-500 ${
                  highlight ? "border-gold shadow-[0_30px_70px_-40px_rgba(199,164,106,0.6)]" : "border-border hover:border-gold/60"
                }`}
              >
                {highlight && (
                  <span className="self-center -mt-14 mb-6 bg-gold text-ink text-[10px] tracking-[0.25em] uppercase px-4 py-2">
                    {t("packages.highlight")}
                  </span>
                )}
                <div className="arched-frame w-36 h-40 mx-auto mb-8 zoom-parent bg-champagne/40">
                  <img src={PACKAGE_IMAGES[key]} alt={pkg.name} loading="lazy" className="w-full h-full object-cover zoom-img" />
                </div>
                <h3 className="font-heading text-3xl text-center mb-2 font-light">{pkg.name}</h3>
                <p className="text-center font-heading text-2xl text-gold mb-8">{price}</p>

                <ul className="space-y-3.5 mb-8 flex-1 text-sm text-ink/80 leading-relaxed">
                  {pkg.features.map((f, j) => (
                    <li key={j} className="flex gap-3">
                      <Check size={16} className="text-gold shrink-0 mt-1" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/anfrage?package=${key}`}
                  data-testid={`package-cta-${key}`}
                  className={`block text-center px-6 py-3.5 text-[11px] tracking-[0.25em] uppercase transition-colors duration-500 ${
                    highlight ? "bg-gold text-ink hover:bg-ink hover:text-cream" : "bg-ink text-cream hover:bg-gold hover:text-ink"
                  }`}
                >
                  {t("packages.cta")}
                </Link>
              </div>
            </Reveal>
          );
        })}
      </div>
      {showNote && (
        <p className="text-center text-xs text-ink-muted mt-10 max-w-2xl mx-auto italic">{t("packages.note")}</p>
      )}
    </div>
  );
}
